import { useEffect } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  selectedIndicesState,
  polygonsState,
} from "../ThreeJs/ReactStates/states";

import {
  copySelectedObstacle,
  pasteCopiedObstacle,
  selectObstacle,
} from "../ThreeJs/Obstacles/ObstacleFunctions";

import { VARS } from "../ThreeJs/Global/variables";

// NEW: segment copy/paste helpers
import {
  copySelectedSegment,
  pasteCopiedSegment,
} from "../ThreeJs/Segments/SegmentCopyPaste";

/**
 * Hotkeys for copying/pasting obstacles AND segments.
 *
 * - Ctrl + C:
 *    - If an obstacle is selected → copy obstacle (existing behavior)
 *    - Else if a segment is selected → copy segment
 *
 * - Ctrl + V:
 *    - If an obstacle was copied → paste obstacle (existing behavior)
 *    - Else if a segment was copied → paste segment
 *
 * Obstacle copy/paste 3D behavior is unchanged.
 */
const ObstacleCopyPasteHotkeys = ({ setPolygonDetails }) => {
  const selectedIndices = useRecoilValue(selectedIndicesState);
  const setSelectedIndices = useSetRecoilState(selectedIndicesState);
  const setPolygons = useSetRecoilState(polygonsState);

  useEffect(() => {
    function onKeyDown(e) {
      const target = e.target;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }

      if (!e.ctrlKey) return;
      const key = e.key.toLowerCase();

      // -------------------------------
      // Ctrl + C → copy
      // -------------------------------
      if (key === "c") {
        // 1️⃣ If an obstacle is selected, keep existing obstacle copy behavior
        if (selectedIndices.obstacleIndex !== null) {
          e.preventDefault();
          copySelectedObstacle(selectedIndices);
          // Last-copy wins semantics: clear segment clipboard
          VARS.copiedSegment = null;
          return;
        }

        // 2️⃣ Otherwise, if a segment is selected, copy the segment
        if (selectedIndices.segmentIndex !== null) {
          e.preventDefault();
          copySelectedSegment(selectedIndices);
          // Last-copy wins semantics: clear obstacle clipboard
          VARS.copiedObstacle = null;
          return;
        }
      }

      // -------------------------------
      // Ctrl + V → paste
      // -------------------------------
      if (key === "v") {
        e.preventDefault();

        // 1️⃣ Prefer obstacle paste if an obstacle was copied
        if (VARS.copiedObstacle) {
          const result = pasteCopiedObstacle(); 
          if (result) {
    setSelectedIndices({
        segmentIndex: result.segmentIndex,
        obstacleIndex: result.obstacleIndex,
        panelIndex: null
    });
}// updates POLYGONS + 3D
          if (!result) return;

          const { segmentIndex, obstacleIndex } = result;
          const data = VARS.copiedObstacle;
          if (!data) return;

          // 🔁 Update Recoil polygonsState so SegmentNavigator sees the new obstacle
          setPolygons((prev) => {
            if (!prev || !prev.length) return prev;

            const next = [...prev];
            const seg = { ...next[segmentIndex] };

            const obsList = Array.isArray(seg.obstructions)
              ? [...seg.obstructions]
              : [];

            // UI only needs minimal info
            obsList.push({
              type: data.type,
              height: data.height,
              offset: data.offset,
            });

            seg.obstructions = obsList;
            next[segmentIndex] = seg;
            return next;
          });

          // Auto-select the new pasted obstacle (3D + UI)
          selectObstacle(
            { segmentIndex, obstacleIndex: null, panelIndex: null },
            obstacleIndex,
            setSelectedIndices
          );
          return;
        }

        // 2️⃣ If there is no obstacle clipboard, but a segment was copied, paste the segment
        if (VARS.copiedSegment) {
          pasteCopiedSegment(setPolygons, setSelectedIndices, setPolygonDetails);
          // Selection + details are handled inside pasteCopiedSegment
          return;
        }
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedIndices, setSelectedIndices, setPolygons, setPolygonDetails]);

  return null;
};

export default ObstacleCopyPasteHotkeys;
